<template>
    <div :class="['slider', {slim: layout.layout === 'default'}, layout.layout, {
        reverse: layout.layout === 'drilldown' && layout.reverse
    }]">
        <Slider
            :slider="layout"
            :idx="idx"
            v-if="layout.slides"
            class="original"
        >
            <template v-slot:header v-if="layout.layout === 'complex'">
                <div class="header">
                    <div class="content">
                        <h2 v-html="layout.title" v-if="layout.title"/>
                        <Parallax class="text">
                            <p v-html="layout.description"/>
                        </Parallax>
                    </div>
                </div>
            </template>
        </Slider>
        <FollowUp v-if="layout.layout === 'complex'" ref="container">
            <template v-slot:main>
                <Ui
                    :slider="layout"
                    :idx="idx"
                    @next="next"
                    @previous="previous"
                />
                <div class="marquee main-marquee">
                    <div
                        class="marquee-gabarit"
                        :style="{
                            webkitTransform: `translateX(${offset}px)`,
                            transform: `translateX(${offset}px)`
                        }"
                    >
                        <span
                            :key="i"
                            v-for="(slide, i) in layout.slides"
                            ref="caption"
                            :class="[
                                'caption',
                                {active: i === idx}
                            ]"
                        >
                            <span class="caption-holder">
                                {{slide.name}}
                            </span>
                        </span>
                    </div>
                </div>
                <div class="sizer">
                    <SplitText :key="currentSlide.id" class="context" :data="currentSlide.description"/>
                        <Cta
                            :type="'router-link'"
                            :to="$trl({
                                name: 'Market',
                                params: {
                                    locale: $route.params.locale,
                                    slug: currentSlide.slug
                                }
                            })"
                            :colors="{ hover: { background: '#114991' } }"
                            class="market-link"
                        >
                            {{$t('cta.showmore')}}
                        </Cta>
                </div>
                <div class="footer main-footer">
                    <div class="gabarit">
                    </div>
                </div>
            </template>
            <template v-slot:details>
                <div class="marquee sub-marquee" ref="dragger">
                    <div
                        class="marquee-gabarit"
                        :style="{
                            webkitTransform: `translateX(${offset}px)`,
                            transform: `translateX(${offset}px)`
                        }"
                    >
                        <router-link
                            :key="i"
                            v-for="(slide, i) in layout.slides"
                            :class="[
                                'caption',
                                {active: i === idx}
                            ]"
                            :to="$trl({
                                name: 'Market',
                                params: {
                                    locale: $route.params.locale,
                                    slug: slide.slug
                                }
                            })"
                        >
                            <span class="caption-holder">
                                {{slide.name}}
                            </span>
                        </router-link>
                    </div>
                </div>
                <transition mode="out-in">
                    <div class="heading" :key="highlightProject.id">
                        <div class="row" v-if="highlightProject">
                            <p class="value">
                                <IconPlus class="plus"/>{{highlightProject.name}}</p>
                            <p class="baseline">{{highlightProject.description}}</p>
                        </div>
                        <div class="row" v-if="country">
                            <span class="headline">{{$t('common.country')}}</span>
                            <p class="value">{{country.name}}</p>
                        </div>
                        <div class="row" v-if="techniques.length > 0">
                            <span class="headline">Techniques</span>
                            <p class="value">
                                <span :key="i" v-for="(technique, i) in techniques">{{technique}}{{(techniques.length - 1 > i) ? ', ' : ''}}</span>
                            </p>
                        </div>
                    </div>
                </transition>
                <div class="footer">
                    <div class="gabarit">
                        <div class="row">
                        </div>
                    </div>
                </div>
            </template>
        </FollowUp>
        <FollowUp v-else-if="layout.layout === 'drilldown'" ref="container">
            <template v-slot:main>
                <div class="title-holder backside" v-if="layout.layout === 'drilldown'">
                    <h2 class="head-title dark">
                        <Parallax :effect="'translateX'" :power="-0.0625">
                            {{layout.title}}
                        </Parallax>
                    </h2>
                </div>
                <div class="cover" v-if="layout.layout === 'drilldown'">
                    <div class="title-holder">
                        <h2 class="head-title">
                            <Parallax :effect="'translateX'" :power="-0.0625">
                                {{layout.title}}
                            </Parallax>
                        </h2>
                    </div>
                </div>
                <Ui
                    :slider="layout"
                    :idx="idx"
                    @next="next"
                    @previous="previous"
                />
                <div class="sizer">
                    <h3 v-if="currentSlide.name">{{currentSlide.name}}</h3>
                    <p
                        class="context"
                        v-html="currentSlide.context || currentSlide.description || currentSlide.caption"
                    ></p>
                    <Cta
                        :type="'router-link'"
                        :to="$trl({
                            name: layout.cta.link
                        })"
                        v-if="layout.cta && layout.cta.display"
                        :colors="{ hover: { background: '#114991' } }"
                    >
                        {{layout.cta.caption}}
                    </Cta>
                </div>
            </template>
            <template v-slot:details>
                <div class="heading">
                    <Slider
                        :slider="layout"
                        :idx="idx"
                        :followup="true"
                        class="backup"
                        v-if="layout.slides"
                    />
                </div>
            </template>
        </FollowUp>
        <FollowUp v-else ref="container">
            <template v-slot:main>
                <Bullet
                    v-if="layout.layout === 'bullet'"
                    :slider="layout"
                    :idx="idx"
                    @set="setIdx"
                />
                <Ui
                    v-else
                    :slider="layout"
                    :idx="idx"
                    @next="next"
                    @previous="previous"
                />
                <span class="slim-caption" v-if="currentSlide && currentSlide.caption">{{currentSlide.caption}}</span>
            </template>
        </FollowUp>
    </div>
</template>

<script>
import MixinSlidable from '@/components/MixinSlidable'
import Parallax from '@/components/animation/Parallax'
import Slider from '@/components/Slider/index'
import FollowUp from '@/components/Slider/FollowUp'
import Bullet from '@/components/Slider/Bullet'
import Ui from '@/components/Slider/Ui'
import Cta from '@/components/Cta'
import IconPlus from '@/components/IconPlus'
import SplitText from '@/components/SplitText'

import Drag from '@/pure/Drag'
export default {
    mixins: [MixinSlidable],
    components: {
        Slider,
        FollowUp,
        Bullet,
        Parallax,
        Ui,
        Cta,
        IconPlus,
        SplitText
    },
    props: {
        layout: {
            type: Object,
            default() {}
        }
    },
    data() {
        return {
            offset: 0
        }
    },
    computed: {
        count() {
            return this.layout.slides.length
        },
        currentSlide() {
            return this.layout.slides[this.idx]
        },
        highlightProject() {
            return this.currentSlide.related_posts ? this.currentSlide.related_posts.find(_ => _.type === 'project') : false
        },
        country() {
            return this.highlightProject ? this.highlightProject.related_posts.find(_ => _.type === 'country') : false
        },
        techniques() {
            const techniques = this.highlightProject ? this.highlightProject.related_posts.filter(_ => _.type === 'technique').map(_ => _.name) : false
            return techniques;
        }
    },
    watch: {
        idx: 'updateOffset'
    },
    mounted() {
        this.handleSize()
        if(this.layout.layout === 'complex' && this.$refs.container && this.$refs.container.$el) {
            // const element = this.$refs.container && this.$refs.container.$el ? this.$refs.container.$el.querySelector('.marquee') : null
            // [].forEach.call(this.$refs.container.$el.querySelectorAll('.marquee'), element => {
            //     this.drag = new Drag({
            //         element,
            //         onDragCb: this.onDrag,
            //         onMoveCb: this.onMove,
            //         onDropCb: this.onDrop,
            //         onSlideCb: this.onSlide
            //     })
            // })
            this.drag = new Drag({
                element: this.$refs.dragger,
                onDragCb: this.onDrag,
                onMoveCb: this.onMove,
                onDropCb: this.onDrop,
                onSlideCb: this.onSlide
            })
        }
        window.addEventListener('resize', this.handleSize, window.captureEvent)
    },
    destroyed() {
        if(this.drag) {
            this.drag.dispose()
        }
    },
    methods: {
        handleSize() {
            const winWidth = this.$el.offsetWidth
            if(this.$refs.caption) {
                this.sizes = this.$refs.caption.map(el => {
                    const { offsetLeft: left, offsetWidth: width } = el
                    return {
                        left: el.offsetLeft,
                        width: el.offsetWidth,
                        offset: (winWidth - width) * .5 - left
                    }
                })
                this.offset = this.sizes[this.idx].offset
            }
        },
        updateOffset() {
            if(this.sizes && !this.drag.isDragin) {
                this.offset = this.sizes[this.idx].offset
            }
        },
        setOffset(offset) {
            if(this.sizes) {
                offset = Math.max(
                    Math.min(offset, this.sizes[0].offset),
                    this.sizes[this.sizes.length - 1].offset
                )
                this.offset = offset
                const near = {
                    id: -1,
                    size: null
                }
                this.sizes.forEach((size, id) => {
                    if(near.id < 0 || Math.abs(size.offset - offset) < Math.abs(near.size.offset - offset)) {
                        near.id = id
                        near.size = size
                    }
                })
            }
        },
        onDrag() {
            this.drag.isDragin = true
            this.$refs.container.$el.classList.add('is-drag')
        },
        onSlide() {
            this.$refs.container.$el.classList.remove('is-drag')
            this.$refs.container.$el.classList.add('is-slide')
        },
        onDrop() {
            this.drag.isDragin = false
            this.$refs.container.$el.classList.remove('is-drag')
            this.$refs.container.$el.classList.remove('is-slide')
            const {offset} = this
            const near = {
                id: -1,
                size: null
            }
            this.sizes.forEach((size, id) => {
                if(near.id < 0 || Math.abs(size.offset - offset) < Math.abs(near.size.offset - offset)) {
                    near.id = id
                    near.size = size
                }
            })
            const veloceX = this.drag.velocity[0]
            const suv = Math.abs(veloceX)
            if(suv > 20) {
                if(veloceX > 0) {
                    this.previous(true)
                } else {
                    this.next(true)
                }
            } else if(this.idx !== near.id) {
                this.setIdx(near.id)
            }
        },
        onMove(x) {
            this.setOffset(this.offset + x)
        }
    }
}
</script>

<style lang="sass" scoped>
@import "@/styles/vars.sass"
.slider
    margin-top: 10%
    margin-bottom: 10%
    &:nth-child(1)
        margin-top: 0
    &.bullet
        margin-bottom: 9.25%
        /deep/ .slider-gabarit
            padding-bottom: 0%
            height: 100vh
            max-height: 1080px
            @include media-breakpoint-down($md)
                height: 0
                padding-bottom: 106.4%
            @media print
                display: none
        /deep/ +.text-block
            @include media-breakpoint-down($md)
                margin-top: 14.25%
            @media print
                margin-top: 2.4cm
        @media print
            margin-bottom: 1.2cm
    &.drilldown
        margin-top: 25%
        margin-bottom: 15%
        @media print
            margin-top: 1.2cm
            margin-bottom: 1.2cm
            .cta
                position: relative
                overflow: visible
                border: none
                &:after
                    content: "https://www.soletanche-bachy.com" attr(href)
                    font-size: 12px
                    font-weight: normal

    // &.bullet,
    &.complex
        @include media-breakpoint-up($xxl)
            margin-left: calc((-100vw + 1920px) * 0.5)
            margin-right: calc((-100vw + 1920px) * 0.5)

.header
    flex: 1
    padding-bottom: 11%
    display: flex
    align-items: center
    justify-content: center
    flex-direction: column
    align-self: stretch

    @include media-breakpoint-down($lg)
        margin-top: 11%
    @media print
        margin-top: 1.2cm
        padding-bottom: 1.2cm
    .content
        width: 38%
        min-width: 400px
        @include media-breakpoint-down($xl)
            font-size: 10.75px
        @include media-breakpoint-down($lg)
            width: 100%
            padding-left: 9.6%
            padding-right: 9.6%
        @include media-breakpoint-down($sm)
            width: auto
            padding-left: 9.6%
            padding-right: 9.6%
            min-width: 100%
        @media print
            padding-left: 9.6%
            padding-right: 9.6%
    h2
        color: #ff2b2a
        font-size: 1.75em
        font-weight: 500
        &+.text
            margin-top: 34%
            @include media-breakpoint-down($lg)
                margin-top: 16%

    .text
        font-size: 3.125em
        font-weight: 500
        line-height: 1.2
        @media print
            transform: none !important
.plus
    position: absolute
    right: 100%
    margin-right: .625em
    top: -0.25em
    @include media-breakpoint-down($sm)
        display: none

    /deep/ path
        fill: #ff2b2a
.heading
    flex: 1
    flex-basis: auto
    .row
        padding-right: 20.83334%
        padding-left: 16.25%
        padding-top: 5.625%
        padding-bottom: 5.625%
        transition: opacity .625s
        @include media-breakpoint-down($lg)
            padding-left: 9.25%
            padding-right: 9.25%
        position: relative
        @for $i from 1 through 4
            &:nth-child(#{$i})
                transition-delay: .1s * $i
        &:nth-child(1)
            padding-top: 14%
        &:nth-child(n+2)
            &:after
                content: ""
                display: block
                height: 2px
                background-image: url('./../../assets/circle.svg')
                background-repeat: repeat-x
                background-size: 12px
                background-position: 0 50%
                position: absolute
                left: 0
                right: 0
                opacity: .3
                top: 0
        .value
            position: relative
            font-size: 50px
            font-weight: 500
            line-height: 1
            margin-top: .1em
            @include media-breakpoint-down($hd)
                font-size: 33.5px
        .baseline
            font-size: 28px
            color: #636772
            margin-top: .625em
            line-height: 1.4285714286

    &.v-enter-active
        transition: visibility 1s
        .row
            @for $i from 1 through 4
                &:nth-child(#{$i})
                    transition-delay: .1s * $i
    // &.v-leave-active
    //     transition: opacity 0s
    &.v-enter
        .row
            opacity: 0
    // &.v-leave-to

.slim-caption
    display: none
    .slim &
        font-weight: 400
        font-size: 28px
        line-height: 1.0714285714
        letter-spacing: -0.03125em
        display: block
        background-color: #ff2b2a
        color: white
        margin-left: 4.25%
        // width: 30.125%
        width: 580px
        padding: 2.875% 3%
        margin-bottom: -5%
        @include media-breakpoint-down($md)
            max-width: 100%
            width: auto
            margin-right: 9.6%
            margin-left: 0
            font-size: 17px

.title-holder
    position: absolute
    left: 0
    margin-left: 12%
    top: 0
    transform: translateY(-50%) translateX(-50%) rotate(-90deg)
    transform-origin: 50% 50%
    pointer-events: none
    @include media-breakpoint-down($lg)
        display: none
    .reverse &
        margin-right: 12%
        transform: translateY(-50%) translateX(50%) rotate(-90deg)
        right: 0
        left: initial
        margin-left: auto
.head-title
    font-size: 50px
    font-weight: 500
    color: #ffffff
    &.dark
        color: #636772
.cover
    position: absolute
    left: 0
    top: -120px
    right: 0
    bottom: 0
    overflow: hidden
    z-index: 1
    pointer-events: none
.backside
    z-index: -1
    top: -120px
.market-link
    margin-top: 4em
    margin-bottom: -1.2em

@media print
    .slider
        &.slim
            display: none
</style>
